<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  edit: [id: Id];
  delete: [id: Id];
}>();

const { t } = useI18n();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed(() => '/index.php/so/item_attributes/lista');

const tableColumns = computed(() => {
  return [
    {
      data: 'so_item_attribute_id',
      title: t('ID'),
      width: 100,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'so_item_attribute_name',
      title: t('Nome'),
      width: 400,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_item_attribute_type',
      title: t('Tipo'),
      width: 200,
      sortable: true,
      filterType: 'text'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_item_attributes_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

const loadData = (): void => {
  const stack = new Error().stack;
  log('SOItemAttributesTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('SOItemAttributesTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de atributos de item'));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOItemAttributesTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar atributos de item') + ': ' + message);
};

const handleEditClick = (id: Id): void => {
  log('SOItemAttributesTable', 'handleEditClick', 'Edit clicked for ID', id);
  emit('edit', id);
};

const handleSaved = (): void => {
  log('SOItemAttributesTable', 'event:so-item-attributes.saved', 'Received');
  loadData();
};

const handleDeleted = (): void => {
  log('SOItemAttributesTable', 'event:so-item-attributes.deleted', 'Received');
  loadData();
};

onMounted(() => {
  log('SOItemAttributesTable', 'mounted', 'Setting up event listeners');
  log('SOItemAttributesTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('so-item-attributes.saved', handleSaved);
  eventBus.on('so-item-attributes.deleted', handleDeleted);
});

onBeforeUnmount(() => {
  log('SOItemAttributesTable', 'beforeUnmount', 'Cleaning up event listeners');

  eventBus.off('so-item-attributes.saved', handleSaved);
  eventBus.off('so-item-attributes.deleted', handleDeleted);
});

defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
